import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { pathParameter, TagEvent, TagPusherService } from '@libs/shared-services';
import { DEFAULT_TEETH } from '@libs/ui-lib';
import { routes } from '@libs/utils';
import { TranslateModule } from '@ngx-translate/core';
import { TeethComponent } from '../teeth';
import { AddTreatmentModalComponent } from '../add-treatment-modal';
import { AddTreatmentModalService } from '../add-treatment-modal/add-treatment-modal.service';
import { TreatmentPlanDialogComponent, TreatmentPlanDialogDataVM } from '../treatment-plan-dialog';
import { ChartingScreenComponentService } from './charting-screen.component.service';

@Component({
  selector: 'lib-charting',
  imports: [CommonModule, TeethComponent, TranslateModule, ReactiveFormsModule, RouterModule],
  templateUrl: './charting-screen.component.html',
  styleUrl: './charting-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [Dialog, ChartingScreenComponentService, AddTreatmentModalService],
})
export class ChartingScreenComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly screenService = inject(ChartingScreenComponentService);
  private readonly dialog = inject(Dialog);
  private readonly tagPusherService = inject(TagPusherService);

  public readonly params = toSignal(this.activatedRoute.paramMap);
  public readonly defaultTeeth = signal(DEFAULT_TEETH);
  public readonly teethWithLayers = computed(() => this.screenService.viewModel().teethWithLayers);
  public readonly bridges = computed(() => this.screenService.viewModel().bridges);
  public readonly loading = computed(() => this.screenService.viewModel().loading);
  public readonly canAddTreatment = computed(() => this.params()!.get(pathParameter('siteId'))! === routes.showcaseSiteId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any, any> | null = null;

  constructor() {}

  public ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(() => {
      const siteId = this.params()!.get(pathParameter('siteId')) ?? undefined;
      const patientId = this.params()!.get(pathParameter('patientId')) ?? undefined;

      if (!this.router.lastSuccessfulNavigation?.previousNavigation) {
        this.tagPusherService.pushTag({
          event: TagEvent.ChartingOpened,
          location: 'other',
          patient_id: patientId!,
        });
      }

      this.screenService.init(siteId, patientId);
      this.dialogRef?.close();
    });
  }

  public ngOnDestroy(): void {
    this.dialog.closeAll();
  }

  public onToothSelected(toothPosition: number): void {
    const siteId = this.params()!.get(pathParameter('siteId')) ?? undefined;
    const patientId = this.params()!.get(pathParameter('patientId')) ?? undefined;
    const teethWithLayers = this.screenService.viewModel().teethWithLayers;
    if (!siteId || !patientId || !teethWithLayers) return;

    this.router.navigate([routes.toothDetails, toothPosition], { relativeTo: this.activatedRoute });
  }

  public onAddTreatmentClick(): void {
    this.dialogRef = this.dialog.open<AddTreatmentModalComponent>(AddTreatmentModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      disableClose: true,
    });
  }

  public onViewTreatmentPlanClick(): void {
    this.dialogRef = this.dialog.open<TreatmentPlanDialogComponent>(TreatmentPlanDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      disableClose: true,
      data: {
        siteId: this.params()!.get(pathParameter('siteId'))!,
        patientId: this.params()!.get(pathParameter('patientId'))!,
      } satisfies TreatmentPlanDialogDataVM,
    });
  }
}
